import { Link } from "@tanstack/react-router";
import { ArrowSquareOut, PencilSimple, Trash, LinkSimple, UploadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { Img } from "@/components/ui/img";
import { VideoPosterFallback } from "@/components/video/VideoPosterFallback";
import { formatDate } from "@/lib/format";
import { formatVideoDuration } from "@/lib/video-embed";
import type { VideoItem } from "@/types";

export function AdminVideoCard({ video, onDelete }: { video: VideoItem; onDelete: () => void }) {
  const duration = formatVideoDuration(video.durationSec);
  const SourceIcon = video.sourceType === "embed" ? LinkSimple : UploadSimple;
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card">
      <Link to="/dashboard/admin/videos/$videoId" params={{ videoId: video.id }} className="relative block aspect-video bg-muted">
        {video.posterUrl ? (
          <Img src={video.posterUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <VideoPosterFallback slug={video.slug} className="h-full w-full" />
        )}
        {duration && (
          <span className="absolute bottom-2 right-2 rounded-md bg-black/75 px-1.5 py-0.5 text-xs font-medium tabular-nums text-white">{duration}</span>
        )}
        <span className="absolute left-2 top-2 flex items-center gap-1 rounded-md bg-white/90 px-1.5 py-0.5 text-[11px] font-medium text-foreground/80">
          <SourceIcon className="size-3" />
          {video.sourceType === "embed" ? "Link" : "Upload"}
        </span>
      </Link>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <StatusBadge status={video.status} />
          <time>{formatDate(video.publishedAt ?? video.createdAt)}</time>
        </div>
        <h3 className="font-semibold leading-snug text-foreground line-clamp-2" style={{ fontFamily: "var(--font-heading)" }}>
          {video.title}
        </h3>
        {video.tags.length > 0 && (
          <p className="text-xs text-muted-foreground line-clamp-1">{video.tags.map((t) => t.name).join(" · ")}</p>
        )}
        <div className="mt-auto flex items-center gap-1 pt-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/dashboard/admin/videos/$videoId" params={{ videoId: video.id }}>
              <PencilSimple className="size-4" />
              Uredi
            </Link>
          </Button>
          {video.status === "published" && (
            <Button asChild variant="ghost" size="sm">
              <a href={`/video/${video.slug}`} target="_blank" rel="noreferrer">
                <ArrowSquareOut className="size-4" />
                Otvori
              </a>
            </Button>
          )}
          <Button variant="ghost" size="sm" className="ml-auto text-rose-600 hover:text-rose-700" onClick={onDelete}>
            <Trash className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
